import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowRight, BookOpen, Shield, Truck, Lock, Code, MapPin } from 'lucide-react';

const Blog: React.FC = () => {
  const blogPosts = [
    {
      title: "5 Ways Vehicle Data APIs Transform Auto Dealerships",
      excerpt: "Discover how modern dealerships are leveraging real-time DMV data to streamline operations and increase sales velocity.",
      date: "March 15, 2025",
      category: "Industry Insights",
      readTime: "5 min read",
      icon: BookOpen
    },
    {
      title: "Understanding DPPA Compliance for Vehicle Data",
      excerpt: "A comprehensive guide to staying compliant with the Driver's Privacy Protection Act when accessing vehicle information.",
      date: "March 10, 2025",
      category: "Compliance",
      readTime: "7 min read",
      icon: Shield
    },
    {
      title: "The Future of Fleet Management Technology",
      excerpt: "How AI and real-time data integration are reshaping the way companies manage their vehicle fleets.",
      date: "March 5, 2025",
      category: "Technology",
      readTime: "6 min read",
      icon: Truck
    },
    {
      title: "Reducing Insurance Fraud with Data Verification",
      excerpt: "Learn how insurance companies are using instant vehicle verification to detect and prevent fraudulent claims.",
      date: "February 28, 2025",
      category: "Security",
      readTime: "4 min read",
      icon: Lock
    },
    {
      title: "API Integration Best Practices for Developers",
      excerpt: "Technical tips and tricks for integrating vehicle data APIs into your existing systems efficiently.",
      date: "February 20, 2025",
      category: "Development",
      readTime: "8 min read",
      icon: Code
    },
    {
      title: "State-by-State DMV Data Access Guide",
      excerpt: "Navigate the complexities of accessing DMV data across all 50 states with our comprehensive guide.",
      date: "February 15, 2025",
      category: "Resources",
      readTime: "10 min read",
      icon: MapPin
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-gray-800/20 via-gray-700/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-black via-gray-900/50 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-br from-gray-900/10 to-gray-800/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <Navigation />

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative">
          <div className="max-w-6xl mx-auto px-8 pt-28 pb-16">
            <div className="text-center">
              <h1 className="text-6xl lg:text-7xl font-black leading-tight tracking-tighter mb-8">
                <span className="block">Industry Insights &</span>
                <span className="block text-white">
                  Updates
                </span>
              </h1>

              <p className="text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto">
                Stay informed with the latest trends, best practices, and insights from the
                vehicle data and automotive technology industry.
              </p>
            </div>
          </div>
        </section>

        {/* Featured Post */}
        <section className="relative py-16">
          <div className="max-w-7xl mx-auto px-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/10 hover:border-[#AD0000]/30 transition-all mb-16">
              <div className="grid md:grid-cols-2 gap-8 p-10">
                <div className="flex flex-col justify-center">
                  <div className="inline-block px-4 py-2 bg-gradient-to-br from-[#AD0000]/10 to-gray-700/20 rounded-full text-sm font-black mb-6 w-fit border border-[#AD0000]/30 text-white">
                    FEATURED
                  </div>
                  <h2 className="text-4xl font-black mb-4">The Complete Guide to Real-Time Vehicle Data Access</h2>
                  <p className="text-lg text-slate-400 mb-6 leading-relaxed">
                    Everything you need to know about accessing, integrating, and leveraging real-time
                    DMV data for your business operations. From API basics to advanced implementations.
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-slate-500 mb-6">
                    <span>March 20, 2025</span>
                    <span>•</span>
                    <span>12 min read</span>
                  </div>
                  <button className="group px-8 py-3 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-red-500/30 flex items-center space-x-2 w-fit">
                    <span>Read Article</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
                <div className="flex items-center justify-center">
                  <div className="w-full h-64 bg-gradient-to-br from-[#AD0000]/10 to-gray-700/20 rounded-2xl flex items-center justify-center border border-white/10">
                    <BookOpen className="w-24 h-24 text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Blog Posts Grid */}
            <h2 className="text-4xl font-black mb-12">
              Recent <span className="text-white">Articles</span>
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {blogPosts.map((post, index) => (
                <div
                  key={index}
                  className="group bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-white/20 transition-all cursor-pointer"
                >
                  <div className="h-48 bg-gradient-to-br from-gray-800/30 to-gray-700/20 flex items-center justify-center border-b border-white/10">
                    <post.icon className="w-16 h-16 text-white group-hover:scale-110 transition-transform" />
                  </div>

                  <div className="p-6">
                    <div className="inline-block px-3 py-1 bg-gradient-to-br from-[#AD0000]/10 to-gray-700/20 rounded-full text-xs font-black mb-4 border border-[#AD0000]/30 text-white">
                      {post.category}
                    </div>

                    <h3 className="text-xl font-black mb-3 line-clamp-2">{post.title}</h3>

                    <p className="text-slate-400 text-sm mb-4 line-clamp-3 leading-relaxed">{post.excerpt}</p>

                    <div className="flex items-center justify-between text-xs text-slate-500 mb-4">
                      <span>{post.date}</span>
                      <span>{post.readTime}</span>
                    </div>

                    <button className="group/btn text-sm font-bold flex items-center space-x-2 text-white hover:text-gray-300 transition-colors">
                      <span>Read More</span>
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="relative py-16 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-8">
            <h2 className="text-4xl font-black mb-12 text-center">
              Browse by <span className="text-white">Category</span>
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                { name: 'Industry Insights', icon: BookOpen, color: 'from-[#AD0000] to-red-700' },
                { name: 'Compliance', icon: Shield, color: 'from-green-500 to-emerald-600' },
                { name: 'Technology', icon: Truck, color: 'from-blue-500 to-cyan-600' },
                { name: 'Security', icon: Lock, color: 'from-yellow-500 to-orange-500' },
                { name: 'Development', icon: Code, color: 'from-purple-500 to-pink-600' },
                { name: 'Resources', icon: MapPin, color: 'from-orange-500 to-red-500' }
              ].map((category, index) => (
                <div
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-white/20 transition-all cursor-pointer text-center group"
                >
                  <div className={`w-12 h-12 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                    <category.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-sm font-black text-white">{category.name}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Signup */}
        <section className="relative py-32">
          <div className="max-w-4xl mx-auto px-8 text-center">
            <h3 className="text-5xl font-black mb-6">Stay Updated</h3>
            <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
              Subscribe to our newsletter for the latest insights, updates, and industry news.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-6 py-4 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#AD0000]/50 focus:ring-2 focus:ring-[#AD0000]/20 transition-all"
              />
              <button className="group px-8 py-4 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 text-white font-bold rounded-xl transition-all shadow-xl shadow-red-500/30 flex items-center justify-center space-x-2 whitespace-nowrap">
                <span>Subscribe</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </section>
      </div>

      <Footer />

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Blog;
